import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, obsAges, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import {FormLabel} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {obsStyles} from '../obsStyle';

function ObsNeuroLimbMovementLegs(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [movement, setMovement] = React.useState(props.movement);
    const [leg, setLeg] = React.useState(props.leg);
    const [secondMovement, setSecondMovement] = React.useState(props.secondMovement);
    const [secondLeg, setSecondLeg] = React.useState(props.secondLeg);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);
    const [optionalLeg, setOptionalLeg] = React.useState(props.optionalLeg);

    const [movements, setMovements] = React.useState([]);

    useEffect(() => {
        let items = [];
        if (type === obsTypes.neuro) {
            if (ageRangeYears === obsAges._16_99 || ageRangeYears === obsAges._5_16 || ageRangeYears === obsAges._1_5) {
                items = [
                    {
                        id: '1',
                        description: t('Normal Power'),
                        // default: true,
                    },
                    {
                        id: '2',
                        description: t('Mild Weakness'),
                    },
                    {
                        id: '3',
                        description: t('Severe Weakness'),
                    },
                    {
                        id: '4',
                        description: t('Extension'),
                    },
                    {
                        id: '5',
                        description: t('No Response'),
                    },
                ];
            }
        }
        setMovements(items);
    }, [type]);

    function setDefaults() {
        let def = _.find(movements, (item) => !!item.default);
        // if (def) {
        setMovement(def);
        setLeg(null);
        setOptionalLeg(config.neuroObsLimbMovExpanded);
        setSecondMovement(def);
        setSecondLeg(null);
        // }
    }

    useEffect(() => {
        if (!movement) {
            setDefaults();
        }
    }, [movements]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (((movement && leg) || (optionalLeg && secondMovement && secondLeg)) && status === obsStatuses.new)
            || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) acceptObs();
    }, [movement, leg, optionalLeg, secondMovement, secondLeg, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    movement,
                    leg,
                    secondMovement,
                    secondLeg,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [
        status,
        movement,
        leg,
        secondMovement,
        secondLeg,
        score,
        accepted,
        acceptDate,
    ]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            // if (movement) {
            //     newScore = movement.score;
            // }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, movement, leg, secondMovement, secondLeg]);

    const onMovementChange = (value) => {
        setMovement(value);
        clearAccepted();
    };

    const onLegChange = (value) => {
        setLeg(value);
        if (value === 'left') {
            setSecondLeg('right');
        } else if (value === 'right') {
            setSecondLeg('left');
        } else {
            setSecondLeg(null);
        }
        clearAccepted();
    };

    const onSecondMovementChange = (value) => {
        setSecondMovement(value);
        clearAccepted();
    };

    const onSecondLegChange = (value) => {
        setSecondLeg(value);
        if (value === 'left') {
            setLeg('right');
        } else if (value === 'right') {
            setLeg('left');
        }
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    const onAddOptionalLeg = () => {
        setOptionalLeg(!optionalLeg);
        setSecondMovement(_.find(movements, (item) => !!item.default));
        setSecondLeg(null);
        clearAccepted();
    };

    const buttons = [];
    if (!config.neuroObsLimbMovExpanded) {
        buttons.push(
            <AgyleButton
                key={'limb-leg-add'}
                size={'normal'}
                variant="outlined"
                colour="primary"
                onClick={onAddOptionalLeg}
            >
                {optionalLeg ? t('Remove Optional Leg') : t('Add Optional Leg')}
            </AgyleButton>,
        );
    }

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12}>
                        <AgyleBlockSwitch
                            onChange={(value) => onLegChange(value)}
                            value={leg}
                            customLabels={{
                                yes: t('Left Leg'),
                                no: t('Right Leg'),
                            }}
                            valueObject={{
                                true: 'left',
                                false: 'right',
                            }}
                            style={{
                                backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} className={classes.component} style={{marginLeft: 10}}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{marginBottom: 15}}
                        >
                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>
                                    {t('Limb Movement - Legs')}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleDictionaryField
                                    onChange={(_, value) => onMovementChange(value)}
                                    options={movements}
                                    value={movement}
                                    getOptionLabel={(option) => option.description}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    extended
                                    noBorder={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {optionalLeg &&
                        <>
                            <Grid item xs={12}>
                                <AgyleBlockSwitch
                                    onChange={(value) => onSecondLegChange(value)}
                                    value={secondLeg}
                                    customLabels={{
                                        yes: t('Left Leg'),
                                        no: t('Right Leg'),
                                    }}
                                    valueObject={{
                                        true: 'left',
                                        false: 'right',
                                    }}
                                    style={{
                                        backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} className={classes.component} style={{marginLeft: 10}}>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    style={{marginBottom: 15}}
                                >
                                    <Grid item xs={12}>
                                        <FormLabel className={classes.label}>
                                            {t('Limb Movement - Legs')}
                                        </FormLabel>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <AgyleDictionaryField
                                            onChange={(_, value) => onSecondMovementChange(value)}
                                            options={movements}
                                            value={secondMovement}
                                            getOptionLabel={(option) => option.description}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            extended
                                            noBorder={false}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    }

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: true,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: !(movement && leg) || (optionalLeg && !(secondMovement && secondLeg)),
                        hideStatuses: true,
                        disableAcceptButtons,
                        customButtons: buttons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsNeuroLimbMovementLegs.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    movement: PropTypes.object,
    optionalLeg: PropTypes.bool,
    leg: PropTypes.oneOf([null, 'left', 'right']),
    secondMovement: PropTypes.object,
    secondLeg: PropTypes.oneOf([null, 'left', 'right']),
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsNeuroLimbMovementLegs.defaultProps = {
    type: obsTypes.neuro,
    status: obsStatuses.new,
    score: 0,
    movement: null,
    optionalLeg: false,
    leg: null,
    secondMovement: null,
    secondLeg: null,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsNeuroLimbMovementLegs);